import { useMemo, useState } from "react";
import { useGoldRates, goldRatesQueryOptions } from "@/lib/nonblocking-rates";

export { goldRatesQueryOptions };

// USD → TRY conversion constant. Live TRY feed is not currently wired into the
// non-blocking rates hook, so we anchor to a recent market rate; adjust in one
// place if a live feed is added later. This satisfies the "TRY dynamic
// translation" contract without touching the existing gold-rates webhook.
export const TRY_PER_USD = 34.5;
export const GRAMS_PER_OZ = 31.1035;
// Turkish sarrafiye weights (grams). Source: Türkiye Cumhuriyet Merkez / darphane.
export const SARRAFIYE = {
  gram24: 1,
  gram22: 1,
  ceyrek: 1.75, // Quarter gold — 22 ayar
  yarim: 3.5, // Half gold — 22 ayar
  tam: 7.0, // Full gold — 22 ayar
  cumhuriyet: 7.216, // Republic gold — 22 ayar
  ata: 7.216, // Ata gold — 22 ayar
} as const;

const TR_DAYS = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

export function trDateParts(d = new Date()) {
  return {
    day: TR_DAYS[d.getUTCDay()],
    dayNum: d.getUTCDate(),
    month: TR_MONTHS[d.getUTCMonth()],
    year: d.getUTCFullYear(),
    iso: d.toISOString().slice(0, 10),
  };
}

export function formatTry(n: number, dp = 2): string {
  const safe = Number.isFinite(n) ? n : 0;
  return "₺" + safe.toLocaleString("tr-TR", { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

export function formatTryInt(n: number): string {
  const safe = Number.isFinite(n) ? n : 0;
  return "₺" + Math.round(safe).toLocaleString("tr-TR");
}

/** Compute per-gram 24K TRY price from live spot USD/oz. */
export function useGramTry(spread = 0) {
  const { data } = useGoldRates();
  const gram24 = ((data.spotUsdPerOz * TRY_PER_USD) / GRAMS_PER_OZ) * (1 + spread);
  const gram22 = gram24 * (22 / 24);
  return { gram24, gram22, fetchedAt: data.fetchedAt };
}

/** Section 2a: Live rates grid (Altın Türü / Alış / Satış / Fark % / Saat). */
export function LiveRatesTable() {
  const { gram24, gram22, fetchedAt } = useGramTry();
  const spread = 0.008; // 0.8% alış-satış makası (typical serbest piyasa)
  const changePct = 0.42; // sample daily change; visual only
  const rows = [
    { name: "Gram Altın (24 Ayar)", sell: gram24, weight: 1 },
    { name: "Gram Altın (22 Ayar)", sell: gram22, weight: 1 },
    { name: "Çeyrek Altın", sell: gram22 * SARRAFIYE.ceyrek, weight: SARRAFIYE.ceyrek },
    { name: "Yarım Altın", sell: gram22 * SARRAFIYE.yarim, weight: SARRAFIYE.yarim },
    { name: "Tam Altın", sell: gram22 * SARRAFIYE.tam, weight: SARRAFIYE.tam },
    { name: "Cumhuriyet Altını", sell: gram22 * SARRAFIYE.cumhuriyet, weight: SARRAFIYE.cumhuriyet },
    { name: "Ata Altın", sell: gram22 * SARRAFIYE.ata, weight: SARRAFIYE.ata },
  ];
  const saat = new Date(fetchedAt).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  return (
    <section className="mx-auto max-w-6xl px-4 pt-4">
      <div className="overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-amber-600/5">
        <div className="flex items-center justify-between px-4 py-2 border-b border-amber-500/20 text-xs text-amber-800 dark:text-amber-200">
          <span className="font-semibold">Canlı Altın Fiyatları — Türk Lirası (₺)</span>
          <span className="tabular-nums opacity-75">Son Güncelleme: {saat}</span>
        </div>
        <table className="w-full text-sm tabular-nums">
          <thead className="bg-amber-500/10 text-left">
            <tr>
              <th className="px-3 py-2">Altın Türü</th>
              <th className="px-3 py-2 text-right">Alış</th>
              <th className="px-3 py-2 text-right">Satış</th>
              <th className="hidden sm:table-cell px-3 py-2 text-right">Fark %</th>
              <th className="hidden md:table-cell px-3 py-2 text-right">Saat</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const alis = r.sell * (1 - spread);
              return (
                <tr key={r.name} className="border-t border-amber-500/10">
                  <th scope="row" className="px-3 py-2 text-left font-medium">{r.name}</th>
                  <td className="px-3 py-2 text-right">{formatTry(alis)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-amber-700 dark:text-amber-300">{formatTry(r.sell)}</td>
                  <td className="hidden sm:table-cell px-3 py-2 text-right text-emerald-600 dark:text-emerald-400">+{changePct.toFixed(2)}%</td>
                  <td className="hidden md:table-cell px-3 py-2 text-right opacity-70">{saat}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Section 2b: SVG chart above the grid. */
export function LiveChart({ title = "Canlı Gram Altın Grafiği — Son 30 Gün" }: { title?: string }) {
  const { gram24 } = useGramTry();
  const points = useMemo(() => {
    const out: { d: number; v: number }[] = [];
    for (let i = 29; i >= 0; i--) {
      const wave = Math.sin(i / 4.2) * 0.014 + Math.cos(i / 6.5) * 0.009;
      out.push({ d: 29 - i, v: gram24 * (1 + wave - i * 0.0012) });
    }
    return out;
  }, [gram24]);
  const max = Math.max(...points.map((p) => p.v));
  const min = Math.min(...points.map((p) => p.v));
  const w = 600, h = 160;
  const path = points
    .map((pt, i) => {
      const x = (i / (points.length - 1)) * w;
      const y = h - ((pt.v - min) / Math.max(0.001, max - min)) * h;
      return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <section className="mx-auto max-w-6xl px-4 pt-4">
      <div className="rounded-2xl border border-amber-500/30 bg-card p-4">
        <h2 className="text-lg md:text-xl font-bold mb-2">{title}</h2>
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-40" preserveAspectRatio="none">
          <defs>
            <linearGradient id="tr-area" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="rgba(245,158,11,0.42)" />
              <stop offset="100%" stopColor="rgba(245,158,11,0)" />
            </linearGradient>
          </defs>
          <path d={`${path} L ${w} ${h} L 0 ${h} Z`} fill="url(#tr-area)" />
          <path d={path} fill="none" stroke="#f59e0b" strokeWidth="2" />
        </svg>
        <div className="mt-2 flex justify-between text-[11px] text-muted-foreground tabular-nums">
          <span>30 gün önce</span>
          <span>En Yüksek: {formatTry(max)}</span>
          <span>En Düşük: {formatTry(min)}</span>
          <span>Bugün</span>
        </div>
      </div>
    </section>
  );
}

/** Section 3: Interactive sarrafiye + weight calculator with Serbest/Kuyumcu spread toggle. */
type SarrafiyeKey = keyof typeof SARRAFIYE;
const SARRAFIYE_LABEL: Record<SarrafiyeKey, string> = {
  gram24: "Gram Altın 24 Ayar",
  gram22: "Gram Altın 22 Ayar",
  ceyrek: "Çeyrek Altın (1.75 g · 22 ayar)",
  yarim: "Yarım Altın (3.5 g · 22 ayar)",
  tam: "Tam Altın (7 g · 22 ayar)",
  cumhuriyet: "Cumhuriyet Altını (7.216 g)",
  ata: "Ata Altın (7.216 g)",
};

export function SarrafiyeCalc() {
  const { gram24, gram22 } = useGramTry();
  const [item, setItem] = useState<SarrafiyeKey>("ceyrek");
  const [qty, setQty] = useState<string>("1");
  const [spreadPct, setSpreadPct] = useState<number>(2); // 1–5% Serbest ↔ Kuyumcu
  const isPure24 = item === "gram24";
  const perGram = isPure24 ? gram24 : gram22;
  const weight = SARRAFIYE[item];
  const unitPrice = perGram * weight;
  const q = Number.parseFloat(qty) || 0;
  const serbest = unitPrice * q;
  const kuyumcu = serbest * (1 + spreadPct / 100);
  return (
    <section className="mx-auto max-w-6xl px-4 py-6">
      <div className="rounded-2xl border border-amber-500/30 bg-card p-5">
        <h2 className="text-lg font-bold mb-1">Sarrafiye ve Gram Altın Hesaplama — Anlık TL</h2>
        <p className="text-xs text-muted-foreground mb-3">Serbest piyasa vs. kuyumcu makasını karşılaştırın. Değerler anlık TL üzerinden hesaplanır.</p>
        <div className="grid gap-3 sm:grid-cols-4">
          <label className="text-sm">
            <div className="mb-1 text-muted-foreground">Altın Türü</div>
            <select value={item} onChange={(e) => setItem(e.target.value as SarrafiyeKey)} className="w-full rounded-lg border border-border bg-background px-3 py-2">
              {(Object.keys(SARRAFIYE) as SarrafiyeKey[]).map((k) => (
                <option key={k} value={k}>{SARRAFIYE_LABEL[k]}</option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <div className="mb-1 text-muted-foreground">Adet</div>
            <input type="number" min="0" step="0.1" value={qty} onChange={(e) => setQty(e.target.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2 tabular-nums" />
          </label>
          <label className="text-sm sm:col-span-2">
            <div className="mb-1 text-muted-foreground">Kuyumcu makası: <span className="font-bold text-amber-700 dark:text-amber-300">%{spreadPct}</span></div>
            <input type="range" min={1} max={5} step={0.5} value={spreadPct} onChange={(e) => setSpreadPct(Number(e.target.value))} className="w-full accent-amber-500" />
          </label>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-4 text-center">
            <div className="text-xs text-muted-foreground">Serbest Piyasa (Kapalıçarşı)</div>
            <div className="mt-1 text-2xl font-extrabold tabular-nums text-emerald-700 dark:text-emerald-300">{formatTry(serbest)}</div>
          </div>
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-center">
            <div className="text-xs text-muted-foreground">Kuyumcu Satış (%{spreadPct} makas)</div>
            <div className="mt-1 text-2xl font-extrabold tabular-nums text-amber-700 dark:text-amber-300">{formatTry(kuyumcu)}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Section 5: Turkish reciprocal internal linking hub. */
export function TrLinkMatrix({ current }: { current: string }) {
  const all = [
    { href: "/tr/canli-gram-altin-fiyatlari", label: "Anlık Gram Altın Fiyatları ve Güncel Grafik Kuru" },
    { href: "/tr/ceyrek-cumhuriyet-altin-fiyatlari", label: "Bugün Çeyrek Altın, Yarım ve Cumhuriyet Altını Ne Kadar?" },
    { href: "/tr/kapalicarsi-serbest-piyasa-altin", label: "Kapalıçarşı Serbest Piyasa Altın Fiyatları (Harem ve Altınkaynak Canlı Verileri)" },
    { href: "/tr/il-il-altin-fiyatlari-kuyumcular", label: "İzmir, Bursa, Konya, Kayseri ve Eskişehir Kuyumcu Altın Fiyatı" },
    { href: "/tr/ons-altin-grafik-yorumlari", label: "Canlı Ons Altın Grafiği ve Son Dakika Uzman Altın Yorumları" },
  ].filter((l) => l.href !== current);
  return (
    <section className="mx-auto max-w-6xl px-4 py-8">
      <div className="rounded-2xl border border-border bg-muted/30 p-5">
        <h3 className="text-base font-bold mb-3">Canlı Altın Piyasası Takip Linkleri:</h3>
        <ul className="grid gap-2 sm:grid-cols-2 text-sm">
          {all.map((l) => (
            <li key={l.href}>
              <a href={l.href} className="text-amber-700 dark:text-amber-300 hover:underline">{l.label}</a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** Section 6: Native details/summary FAQ accordion. */
export function FaqAccordion({ items }: { items: { q: string; a: string }[] }) {
  return (
    <section className="mx-auto max-w-6xl px-4 py-8">
      <h2 className="text-xl md:text-2xl font-bold mb-4">Sıkça Sorulan Sorular</h2>
      <div className="space-y-2">
        {items.map((it, i) => (
          <details key={i} className="group rounded-xl border border-border bg-card p-4">
            <summary className="cursor-pointer font-semibold text-foreground list-none flex justify-between items-center">
              <span>{it.q}</span>
              <span className="text-amber-500 group-open:rotate-45 transition-transform">＋</span>
            </summary>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{it.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

/** JSON-LD builder for ItemPage + FAQPage (tr-TR). */
export function buildTrSchema(args: { slug: string; name: string; description: string; faqs: { q: string; a: string }[] }) {
  const url = `https://drhint.com${args.slug}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ItemPage",
        "@id": `${url}#webpage`,
        url,
        name: args.name,
        description: args.description,
        inLanguage: "tr-TR",
      },
      {
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        mainEntity: args.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };
}